/*
 * ===========================================================================
 * Nosh — Razorpay availability guard
 * ===========================================================================
 * Keeps the [data-nosh-rzp] attribute on .product-form__buttons in sync with
 * Dawn's canonical submit button, so assets/nosh-razorpay-guard.css can hide
 * Razorpay's injected purchase area whenever the selected variant cannot be
 * bought.
 *
 * SCOPE DISCIPLINE
 * This file writes exactly one attribute. It never touches Razorpay's markup,
 * script, configuration or checkout; never alters price, inventory or variant
 * data; and never interferes with Dawn's product form, cart or add-to-cart
 * flow.
 *
 * AVAILABILITY RULE
 *   unavailable  <=>  submitButton.disabled === true
 *                     AND the button is not in Dawn's transient loading state.
 *
 * Dawn's product-form.js sets the `disabled` attribute while an add-to-cart
 * request is in flight and simultaneously adds the `loading` class. Excluding
 * that state stops Razorpay's button from flickering away mid-add on a product
 * that is perfectly purchasable.
 *
 * MULTI-VARIANT SAFETY
 * Dawn re-renders the product-form region on every variant change, so the guard
 * re-reads the freshly rendered button rather than caching a variant list. The
 * current catalogue has one variant per product, but this stays correct without
 * modification if that changes.
 */
(function () {
  'use strict';

  var STATE_ATTR = 'data-nosh-rzp';
  var SUBMIT_PREFIX = 'ProductSubmitButton-';
  var pending = null;

  function isLoading(button) {
    return (
      button.classList.contains('loading') ||
      button.getAttribute('aria-busy') === 'true'
    );
  }

  function stateFor(button) {
    if (!button) return null;
    return button.disabled && !isLoading(button) ? 'unavailable' : 'available';
  }

  function sync() {
    pending = null;
    var wrappers = document.querySelectorAll('.product-form__buttons');
    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      var button = wrapper.querySelector('[id^="' + SUBMIT_PREFIX + '"]');
      var state = stateFor(button);
      if (state && wrapper.getAttribute(STATE_ATTR) !== state) {
        wrapper.setAttribute(STATE_ATTR, state);
      }
    }
  }

  /* Coalesce bursts of DOM activity into a single pass. setTimeout rather than
     requestAnimationFrame so the guard still settles in backgrounded tabs,
     where rAF never fires. */
  function scheduleSync() {
    if (pending !== null) return;
    pending = window.setTimeout(sync, 0);
  }

  function observe() {
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var record = records[i];
        if (record.type === 'attributes') {
          var target = record.target;
          if (target.id && target.id.indexOf(SUBMIT_PREFIX) === 0) {
            scheduleSync();
            return;
          }
          continue;
        }
        if (record.addedNodes.length || record.removedNodes.length) {
          scheduleSync();
          return;
        }
      }
    });

    /* STATE_ATTR is deliberately absent from attributeFilter, so the guard's own
       writes cannot retrigger the observer. */
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled', 'class', 'aria-disabled', 'aria-busy']
    });
  }

  function init() {
    sync();
    observe();
    /* Variant pickers commit through change events; re-read once Dawn has
       finished swapping the section markup. */
    document.addEventListener('change', scheduleSync, true);
    window.addEventListener('pageshow', sync);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
